import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import useNetworkStatus from './hooks/useNetworkStatus';
import Login from './components/Login';
import Layout from './components/Layout';
import NoInternet from './components/NoInternet';
import TokenDebug from './components/TokenDebug';
// Import network testing utilities for development
import './utils/networkTest';
import Dashboard from './pages/Dashboard';
import RoleMaster from './pages/RoleMaster';
import SizeMaster from './pages/SizeMaster';
import ColorMaster from './pages/ColorMaster';
import CategoryMaster from './pages/CategoryMaster';
import MenuMaster from './pages/MenuMaster';
import BrandMaster from './pages/BrandMaster';
import SimpleCategoryMaster from './pages/SimpleCategoryMaster';
import GenderMaster from './pages/GenderMaster';
import FabricMaster from './pages/FabricMaster';
import WorkMaster from './pages/WorkMaster';
import TimesWornMaster from './pages/TimesWornMaster';
import ConditionGradeMaster from './pages/ConditionGradeMaster';
import DefectMaster from './pages/DefectMaster';
import BranchMaster from './pages/BranchMaster';
import EmployeeMaster from './pages/EmployeeMaster';
import CustomerReviews from './pages/CustomerReviews';
import Listings from './pages/Listings';
import ListingDetails from './pages/ListingDetails';
import PurchasedItems from './pages/PurchasedItems';
import StoreManagerPortal from './pages/StoreManagerPortal';
import UpcomingDeliveries from './pages/UpcomingDeliveries';
import BranchStock from './pages/BranchStock';
// removed AuthenticatorPortal
import AwbMaster from './pages/AwbMaster';
import ContentPolicies from './pages/ContentPolicies';
import ListingsRoute from './components/ListingsRoute';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user } = useAuth();
  return user ? children : <Navigate to="/login" replace />;
};

// Public Route Component (redirect to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user } = useAuth();
  return !user ? children : <Navigate to="/dashboard" replace />;
};

function App() {
  const { isOnline, wasOffline } = useNetworkStatus();
  const [isRetrying, setIsRetrying] = useState(false);

  const handleRetry = async () => {
    setIsRetrying(true);
    
    // Simulate checking connectivity
    setTimeout(() => {
      setIsRetrying(false);
      // Force a page reload to re-check connectivity
      window.location.reload();
    }, 2000);
  };

  return (
    <AuthProvider>
      <Router>
        <div className="App">
          {/* <TokenDebug /> */}
          <Toaster 
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },
            }}
          />
          
          {/* Show No Internet screen when offline */}
          {!isOnline && (
            <NoInternet 
              onRetry={handleRetry} 
              isRetrying={isRetrying}
            />
          )}
          
          {/* Show reconnection notification */}
          {isOnline && wasOffline && (
            <NoInternet showReconnected={true} />
          )}
          
          <Routes>
            <Route 
              path="/login" 
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              } 
            />
            <Route 
              path="/" 
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
                <Route path="listings" element={<ListingsRoute />} />
                <Route path="listing-details/:listingId" element={<ListingDetails />} />
                <Route path="purchased-items" element={<PurchasedItems />} />
                <Route path="store-manager" element={<StoreManagerPortal />} />
                <Route path="upcoming-deliveries" element={<UpcomingDeliveries />} />
                <Route path="branch-stock" element={<BranchStock />} />
                <Route path="awb-master" element={<AwbMaster />} />
                {/* authenticator route removed */}
              <Route path="roles" element={<RoleMaster />} />
              {/* Removed Store Branches and User Management routes */}
              <Route path="sizes" element={<SizeMaster />} />
              <Route path="colors" element={<ColorMaster />} />
              <Route path="categories" element={<CategoryMaster />} />
              <Route path="menus" element={<MenuMaster />} />
              <Route path="brands" element={<BrandMaster />} />
              <Route path="gender-masters" element={<GenderMaster />} />
              <Route path="simple-categories" element={<SimpleCategoryMaster />} />
              <Route path="fabrics" element={<FabricMaster />} />
              <Route path="works" element={<WorkMaster />} />
              <Route path="times-worn" element={<TimesWornMaster />} />
              <Route path="condition-grades" element={<ConditionGradeMaster />} />
              <Route path="defects" element={<DefectMaster />} />
              <Route path="branches" element={<BranchMaster />} />
              <Route path="employees" element={<EmployeeMaster />} />
              <Route path="customer-reviews" element={<CustomerReviews />} />
              <Route path="content-policies" element={<ContentPolicies />} />
            </Route>
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
